/**
 * useGtCtsReviewerSession — GroundTruth CTS reviewer session.
 *
 * Thin adapter over useReviewerSession (the shared CLW reviewer transport in
 * hooks/useReviewerSession.js). Supplies the GT route, the create-payload
 * validation, the auto-start readiness check, and the `review_cts.updated`
 * event reducer. The return shape is unchanged from when this hook was
 * standalone, so CampaignLaunchWorkflow consumes it exactly as before:
 *   { sessionId, messages, suggestions, status, error, connectionError,
 *     approvedMarker, usage, artifactErrors, interrupting, pendingToolCalls,
 *     latestReviewCts, latestAppliedEdits, latestConfigGcsPath,
 *     latestReviewCtsGcsPath, latestCommittedAt, lastApplyFailure, lastEditStaged,
 *     createSession, connect, reconnect, sendMessage, interrupt, terminate,
 *     newSession, STATUS }
 *
 * Backend routes (all under the route base):
 *   POST   /api/v2/gt-cts-reviewer-sessions
 *   GET    /api/v2/gt-cts-reviewer-sessions/:id  (+ /events, /stream)
 *   POST   /api/v2/gt-cts-reviewer-sessions/:id/{message,interrupt,terminate}
 */

import { useReviewerSession } from './useReviewerSession';

const ROUTE_BASE = '/api/v2/gt-cts-reviewer-sessions';

export function useGtCtsReviewerSession({ accountId, userId, autoStart = false, createPayload }) {
  return useReviewerSession({
    accountId,
    userId,
    autoStart,
    createPayload,
    routeBase: ROUTE_BASE,
    logName: 'useGtCtsReviewerSession',
    validateCreatePayload: (p) => (p?.excelGcsPath ? null : 'Missing excelGcsPath'),
    isAutoStartReady: (p) => Boolean(
      p?.excelGcsPath && (p?.reviewCtsJson || p?.reviewCtsGcsPath || p?.requestId),
    ),
    autoStartDeps: [
      createPayload?.reviewCtsJson,
      createPayload?.requestId,
      createPayload?.reviewCtsGcsPath,
      createPayload?.excelGcsPath,
    ],
    initialPlatformState: {
      latestReviewCts: null,
      latestAppliedEdits: [],
      latestConfigGcsPath: null,
      latestReviewCtsGcsPath: null,
      latestCommittedAt: null,
    },
    // Agent applied a JSON edit; backend re-ran the validator and pushed the
    // fresh payload + applied edits so far. Parent consumes via useEffect →
    // swaps ctsReview / reviewRows and updates the launch GCS path.
    reducePlatformEvent: (data) => {
      if (data.type !== 'review_cts.updated') return null;
      const patch = {};
      if (data.review_cts) patch.latestReviewCts = data.review_cts;
      if (Array.isArray(data.applied_edits)) patch.latestAppliedEdits = data.applied_edits;
      if (data.new_config_gcs_path) patch.latestConfigGcsPath = data.new_config_gcs_path;
      if (data.edited_review_cts_gcs_path) patch.latestReviewCtsGcsPath = data.edited_review_cts_gcs_path;
      // Set alongside review_cts (null for an older backend), so a replayed
      // commit can be told apart from a fresh one.
      if (data.review_cts) patch.latestCommittedAt = data.committed_at || null;
      return Object.keys(patch).length ? patch : null;
    },
  });
}
